import { useCallback, useEffect, useRef, useState } from 'react'

// Curseur de scroll "overlay" façon VS Code : masqué au repos, apparaît au
// survol ou pendant le scroll puis se refond en douceur, sans jamais réserver
// de place (contrairement à une vraie scrollbar, dont la largeur varie selon
// l'OS/souris-vs-trackpad). `wrapperRef` est le conteneur position:relative
// qui accueille le curseur ; par défaut c'est aussi lui qui scrolle, ou passe
// `scrollSelector` (ex. '.ant-table-body') quand l'élément qui scrolle est un
// descendant généré par une lib tierce.
export const useOverlayScrollbarThumb = (wrapperRef, { scrollSelector, deps = [] } = {}) => {
  const scrollElRef = useRef(null);
  const hideTimeoutRef = useRef();
  const [thumb, setThumb] = useState(null);
  const [visible, setVisible] = useState(false);

  const updateThumb = useCallback(() => {
    const scrollEl = scrollElRef.current;
    const wrapperEl = wrapperRef.current;
    if (!scrollEl || !wrapperEl) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    if (scrollHeight <= clientHeight) {
      setThumb(null);
      return;
    }
    const thumbHeight = Math.max(24, (clientHeight * clientHeight) / scrollHeight);
    const maxTop = clientHeight - thumbHeight;
    const thumbTop = maxTop * (scrollTop / (scrollHeight - clientHeight));
    // getBoundingClientRect plutôt que offsetTop : fiable même si un ancêtre
    // intermédiaire (lib tierce) est lui-même positionné.
    const scrollRect = scrollEl.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    setThumb({
      top: scrollRect.top - wrapperRect.top + thumbTop,
      height: thumbHeight,
      // L'élément qui scrolle n'est pas forcément collé au bord droit du wrapper.
      right: wrapperRect.right - scrollRect.right,
    });
  }, [wrapperRef]);

  useEffect(() => {
    const scrollEl = scrollSelector
      ? wrapperRef.current?.querySelector(scrollSelector)
      : wrapperRef.current;
    scrollElRef.current = scrollEl;
    if (!scrollEl) return;
    const onScroll = () => {
      updateThumb();
      setVisible(true);
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => setVisible(false), 800);
    };
    scrollEl.addEventListener('scroll', onScroll);
    window.addEventListener('resize', updateThumb);
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateThumb);
      clearTimeout(hideTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, scrollSelector, updateThumb]);

  useEffect(() => {
    updateThumb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    thumb,
    visible,
    // À poser sur le conteneur (wrapperRef) pour afficher le curseur au survol.
    onMouseEnter: () => setVisible(true),
    onMouseLeave: () => setVisible(false),
  };
};
